import type {IRoutesManager, OmitStrict} from '@wix/devzai-utils-common';
import {RoutesManager} from '@wix/devzai-utils-common';
import type {DoppeViewerPageInfo} from '../viewer-pages/doppe-viewer-pages';
import {DoppeViewerPageType} from '../viewer-pages/doppe-viewer-pages';

/**
 * Type helper to extract params from page info (excluding 'pageType' discriminator)
 */
export type DoppeViewerPageParams<PAGE_INFO extends DoppeViewerPageInfo> = OmitStrict<PAGE_INFO, 'pageType'>;

export const DoppeViewerCrossPagesQueryParams = [
    'petri_ovr', 'x-wix-route', 'demo-param', 'preview'
]

/**
 * First-segment URL paths reserved for vertical pages in the viewer.
 * Content pages must not use these as slugs — they would be unreachable
 * because vertical routes are matched before the content page catch-all.
 */
export const DoppeViewerReservedPageSlugs = [
    'products',
    'blog',
    'events',
    'services',
    'cart',
    'plans',
    'pricing-plans',
] as const;

/**
 * Viewer Pages Routes Manager
 * 
 * Manages bidirectional routing for the viewer (URL ↔ Page Info).
 * Uses constant paths for vertical pages and dynamic slugs for content pages.
 * 
 * ## URL Patterns
 * 
 * The viewer uses clean, constant URLs with the following patterns:
 * 
 * ```
 * Home:                   /
 * Content pages:          /:slug              (e.g., /about, /contact)
 * 
 * Products list:          /products
 * Product item:           /product/:slug
 * 
 * Blog posts list:        /blog
 * Blog post item:         /post/:slug
 * 
 * Events list:            /events
 * Event item:             /event/:slug
 * 
 * Services list:          /services
 * Service item:           /service/:slug
 * ```
 * 
 * ## Design Philosophy
 * 
 * - **Constant paths** for vertical pages (products, blog, events, services)
 * - **No page IDs in URLs** - page IDs resolved internally from default pages
 * - **Dynamic slugs** only for content pages and vertical items
 * - **Simple, predictable URLs** that are easy to remember and share
 * 
 * ## Route Registration Order
 * 
 * Routes are registered in specific-to-generic order to ensure correct matching:
 * 1. Home page (exact match: /)
 * 2. Item pages (/product/:slug, /post/:slug, etc.) - specific
 * 3. List pages (/products, /blog, etc.) - constant paths
 * 4. Content pages (/:slug) - catch-all for custom content
 * 
 * @example
 * ```typescript
 * const routesManager = new DoppeViewerPagesRoutesManager({ basePath: '' });
 * 
 * // Generate URLs - no page IDs needed
 * routesManager.resolvePath({ pageType: 'ProductsList' });
 * // → '/products'
 * 
 * routesManager.resolvePath({ pageType: 'ProductItem', productSlug: 'my-product' });
 * // → '/product/my-product'
 * 
 * // Content pages still use slugs
 * routesManager.resolvePath({ pageType: 'ContentPage', slug: 'about' });
 * // → '/about'
 * ```
 */
export class DoppeViewerPagesRoutesManager implements IRoutesManager.Initialized<DoppeViewerPageInfo> {
    
    private routesManager: IRoutesManager<DoppeViewerPageInfo>;
    
    constructor(options: RoutesManager.Options = {}) {
        this.routesManager = RoutesManager.create<DoppeViewerPageInfo>(options);
        
        this.registerRoutes();
    }
    
    /**
     * Register a route with automatic param type inference
     * 
     * @template PAGE_INFO - The specific page info interface (e.g., DoppeViewerPageInfo.ProductItem)
     * @template PARAMS - URL route params (defaults to all PAGE_INFO fields except 'pageType')
     * @param options - Route configuration
     */
    private registerRoute<
        PAGE_INFO extends DoppeViewerPageInfo,
        PARAMS extends {} = DoppeViewerPageParams<PAGE_INFO>
    >(
        options: {
            path: string;
            pageType: PAGE_INFO['pageType'];
            createPageInfo: (params: PARAMS) => PAGE_INFO;
            extractParams?: (routeInfo: PAGE_INFO) => PARAMS;
        }
    ): void {
        const { path, pageType, createPageInfo, extractParams } = options;
        
        this.routesManager.registerRoute<PARAMS>({
            path: path,
            resolveRouteInfoFromParams: (match): PAGE_INFO => {
                return createPageInfo(match as PARAMS);
            },
            resolveParams: (routeInfo) => {
                if (routeInfo.pageType !== pageType) {
                    return null;
                }
                
                if (extractParams) {
                    // Use custom param extraction if provided
                    return extractParams(routeInfo as PAGE_INFO);
                } else {
                    // Default: Extract all properties except 'pageType'
                    const { pageType: _pageType, ...params } = routeInfo as PAGE_INFO;
                    return params as unknown as PARAMS;
                }
            }
        });
    }
    
    /**
     * Register all viewer routes
     */
    private registerRoutes(): void {
        
        // ========================================================================
        // Route Registration
        // Order: Specific routes first, then generic routes
        // ========================================================================
        
        // ------------------------------------------------------------------------
        // 1. Home Page
        // ------------------------------------------------------------------------
        
        this.registerRoute<DoppeViewerPageInfo.Home>({
            path: '/',
            pageType: DoppeViewerPageType.Home,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.Home
                };
            }
        });
        
        // ------------------------------------------------------------------------
        // 2. Item Pages (Specific - must be registered before list pages)
        // Use constant paths like /product/:slug instead of /:pageId/:slug
        // Page IDs are resolved internally based on default pages (defaultProductPage, etc.)
        // When no slug is provided, the page will load the first available item.
        // ------------------------------------------------------------------------
        
        // Product Item: /product/:productSlug or /product (for first product)
        this.registerRoute<DoppeViewerPageInfo.ProductItem, { productSlug: string }>({
            path: '/product(/:productSlug)',
            pageType: DoppeViewerPageType.ProductItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ProductItem,
                    productSlug: params.productSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    productSlug: routeInfo.productSlug
                };
            }
        });
        
        // Digital Product Item: /digital-product/:productSlug or /digital-product (for preview)
        this.registerRoute<DoppeViewerPageInfo.DigitalProductItem, { productSlug: string }>({
            path: '/digital-product(/:productSlug)',
            pageType: DoppeViewerPageType.DigitalProductItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.DigitalProductItem,
                    productSlug: params.productSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    productSlug: routeInfo.productSlug ?? ''
                };
            }
        });
        
        // Blog Post Item: /post/:postSlug or /post (for first blog post)
        this.registerRoute<DoppeViewerPageInfo.BlogPostItem, { postSlug: string }>({
            path: '/post(/:postSlug)',
            pageType: DoppeViewerPageType.BlogPostItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.BlogPostItem,
                    postSlug: params.postSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    postSlug: routeInfo.postSlug ?? ''
                };
            }
        });
        
        // RSVP Event Item: /rsvp-event/:eventSlug or /rsvp-event (for first event)
        this.registerRoute<DoppeViewerPageInfo.EventItem, { eventSlug: string }>({
            path: '/rsvp-event(/:eventSlug)',
            pageType: DoppeViewerPageType.EventItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.EventItem,
                    eventSlug: params.eventSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    eventSlug: routeInfo.eventSlug ?? ''
                };
            }
        });
        
        // Ticketing Event Item: /ticket-event/:eventSlug or /ticket-event (for preview with dummy tickets)
        this.registerRoute<DoppeViewerPageInfo.TicketingEventItem, { eventSlug: string }>({
            path: '/ticket-event(/:eventSlug)',
            pageType: DoppeViewerPageType.TicketingEventItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.TicketingEventItem,
                    eventSlug: params.eventSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    eventSlug: routeInfo.eventSlug ?? ''
                };
            }
        });
        
        // Service Item: /service/:serviceSlug or /service (for first service)
        this.registerRoute<DoppeViewerPageInfo.ServiceItem, { serviceSlug: string }>({
            path: '/service(/:serviceSlug)',
            pageType: DoppeViewerPageType.ServiceItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ServiceItem,
                    serviceSlug: params.serviceSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    serviceSlug: routeInfo.serviceSlug
                };
            }
        });
        
        // Course Item: /course/:serviceSlug or /course (for first course)
        this.registerRoute<DoppeViewerPageInfo.CourseItem, { serviceSlug: string }>({
            path: '/course(/:serviceSlug)',
            pageType: DoppeViewerPageType.CourseItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.CourseItem,
                    serviceSlug: params.serviceSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    serviceSlug: routeInfo.serviceSlug
                };
            }
        });
        
        // Appointment Item: /appointment/:serviceSlug or /appointment (for first appointment)
        this.registerRoute<DoppeViewerPageInfo.AppointmentItem, { serviceSlug: string }>({
            path: '/appointment(/:serviceSlug)',
            pageType: DoppeViewerPageType.AppointmentItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.AppointmentItem,
                    serviceSlug: params.serviceSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    serviceSlug: routeInfo.serviceSlug
                };
            }
        });
        
        // Class Item: /class/:serviceSlug or /class (for first class)
        this.registerRoute<DoppeViewerPageInfo.ClassItem, { serviceSlug: string }>({
            path: '/class(/:serviceSlug)',
            pageType: DoppeViewerPageType.ClassItem,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ClassItem,
                    serviceSlug: params.serviceSlug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    serviceSlug: routeInfo.serviceSlug
                };
            }
        });
        
        // ------------------------------------------------------------------------
        // 3. List Pages (Constant paths - registered before content pages)
        // Use constant paths like /products instead of /:slug
        // ------------------------------------------------------------------------
        
        // Products List: /products
        this.registerRoute<DoppeViewerPageInfo.ProductsList>({
            path: '/products',
            pageType: DoppeViewerPageType.ProductsList,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.ProductsList
                };
            }
        });
        
        // Blog Posts List: /blog
        this.registerRoute<DoppeViewerPageInfo.BlogPostsList>({
            path: '/blog',
            pageType: DoppeViewerPageType.BlogPostsList,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.BlogPostsList
                };
            }
        });
        
        // Events List: /events
        this.registerRoute<DoppeViewerPageInfo.EventsList>({
            path: '/events',
            pageType: DoppeViewerPageType.EventsList,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.EventsList
                };
            }
        });
        
        // Services List: /services
        this.registerRoute<DoppeViewerPageInfo.ServicesList>({
            path: '/services',
            pageType: DoppeViewerPageType.ServicesList,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.ServicesList
                };
            }
        });

        // Cart Page: /cart
        this.registerRoute<DoppeViewerPageInfo.Cart>({
            path: '/cart',
            pageType: DoppeViewerPageType.Cart,
            createPageInfo: () => {
                return {
                    pageType: DoppeViewerPageType.Cart
                };
            }
        });
        
        // ------------------------------------------------------------------------
        // 4. Content Pages (Generic - registered last)
        // Pattern: /:slug for custom content pages like /about, /contact
        // ------------------------------------------------------------------------
        
        // Content Page: /:slug (catch-all for content pages)
        // Note: pageId can be looked up from slug using bymoPagePropsGetInnerPageBySlug()
        this.registerRoute<DoppeViewerPageInfo.ContentPage, { slug: string }>({
            path: '/:slug',
            pageType: DoppeViewerPageType.ContentPage,
            createPageInfo: (params) => {
                return {
                    pageType: DoppeViewerPageType.ContentPage,
                    slug: params.slug
                };
            },
            extractParams: (routeInfo) => {
                return {
                    slug: routeInfo.slug
                };
            }
        });
    }
    
    // ========================================================================
    // Core Methods (delegating to routes manager)
    // ========================================================================
    
    /**
     * Generate a URL path from page info
     * @throws Error if no route matches the page info
     */
    public resolvePath(routeInfo: DoppeViewerPageInfo): string {
        return this.routesManager.resolvePath(routeInfo);
    }
    
    /**
     * Parse a URL path to page info
     * 
     * NOTE: Due to multiple routes sharing the same URL pattern,
     * this method cannot reliably distinguish between different page types
     * (e.g., ProductsList vs ContentPage both use /page/:slug).
     * 
     * Use DoppeViewerRouterClientService.resolvePageInfoFromUrl() instead,
     * which considers bymoPageProps context to determine the correct type.
     * 
     * @returns Page info if route matches, null otherwise
     */
    public resolveRouteInfo(path: string, query: Record<string, string> | string): DoppeViewerPageInfo | null {
        return this.routesManager.resolveRouteInfo(path, query);
    }
    
    /**
     * Safely generate a URL path from page info
     * @returns URL path or null if no route matches
     */
    public tryResolvePath(routeInfo: DoppeViewerPageInfo): string | null {
        return this.routesManager.tryResolvePath(routeInfo);
    }
    
    // ========================================================================
    // Helper Methods (type-safe URL generation)
    // ========================================================================
    
    /**
     * Generate URL for home page
     * @returns URL path (e.g., '/')
     */
    public homePage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.Home
        });
    }
    
    /**
     * Generate URL for content page
     * @param options - Page parameters
     * @returns URL path (e.g., '/about')
     */
    public contentPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.ContentPage>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ContentPage,
            slug: options.slug
        });
    }
    
    /**
     * Generate URL for products list page
     * @returns URL path (e.g., '/products')
     */
    public productsListPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ProductsList
        });
    }
    
    /**
     * Generate URL for product item page
     * @param options - Page parameters (optional slug, defaults to first product)
     * @returns URL path (e.g., '/product/my-product' or '/product')
     */
    public productItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.ProductItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ProductItem,
            productSlug: options.productSlug
        });
    }
    
    /**
     * Generate URL for blog posts list page
     * @returns URL path (e.g., '/blog')
     */
    public blogPostsListPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.BlogPostsList
        });
    }
    
    /**
     * Generate URL for blog post item page
     * @param options - Page parameters
     * @returns URL path (e.g., '/post/my-post')
     */
    public blogPostItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.BlogPostItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.BlogPostItem,
            postSlug: options.postSlug
        });
    }
    
    /**
     * Generate URL for events list page
     * @returns URL path (e.g., '/events')
     */
    public eventsListPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.EventsList
        });
    }
    
    /**
     * Generate URL for event item page
     * @param options - Page parameters
     * @returns URL path (e.g., '/event/conference-2024')
     */
    public eventItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.EventItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.EventItem,
            eventSlug: options.eventSlug
        });
    }
    
    /**
     * Generate URL for services list page
     * @returns URL path (e.g., '/services')
     */
    public servicesListPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ServicesList
        });
    }
    
    /**
     * Generate URL for service item page
     * @param options - Page parameters (optional slug, defaults to first service)
     * @returns URL path (e.g., '/service/consultation' or '/service')
     */
    public serviceItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.ServiceItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ServiceItem,
            serviceSlug: options.serviceSlug
        });
    }
    
    /**
     * Generate URL for course item page
     * @param options - Page parameters (optional slug, defaults to first course)
     * @returns URL path (e.g., '/course/photography-101' or '/course')
     */
    public courseItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.CourseItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.CourseItem,
            serviceSlug: options.serviceSlug
        });
    }
    
    /**
     * Generate URL for appointment item page
     * @param options - Page parameters (optional slug, defaults to first appointment)
     * @returns URL path (e.g., '/appointment/consultation' or '/appointment')
     */
    public appointmentItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.AppointmentItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.AppointmentItem,
            serviceSlug: options.serviceSlug
        });
    }
    
    /**
     * Generate URL for class item page
     * @param options - Page parameters (optional slug, defaults to first class)
     * @returns URL path (e.g., '/class/yoga-basics' or '/class')
     */
    public classItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.ClassItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.ClassItem,
            serviceSlug: options.serviceSlug
        });
    }

    /**
     * Generate URL for digital product item page
     * @param options - Page parameters (optional slug, defaults to first digital product)
     * @returns URL path (e.g., '/digital-product/my-ebook' or '/digital-product')
     */
    public digitalProductItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.DigitalProductItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.DigitalProductItem,
            productSlug: options.productSlug
        });
    }

    /**
     * Generate URL for ticketing event item page
     * @param options - Page parameters (optional slug, defaults to first ticketing event)
     * @returns URL path (e.g., '/ticket-event/concert-2024' or '/ticket-event')
     */
    public ticketingEventItemPage(options: DoppeViewerPageParams<DoppeViewerPageInfo.TicketingEventItem>): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.TicketingEventItem,
            eventSlug: options.eventSlug
        });
    }

    /**
     * Generate URL for cart page
     * @returns URL path (e.g., '/cart')
     */
    public cartPage(): string {
        return this.resolvePath({
            pageType: DoppeViewerPageType.Cart
        });
    }
}
